import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Camera, Paperclip, Save, QrCode, FileText, Trash2, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { displayStatus, ConsequenceBadge } from "@/lib/capitalLabels";
import { toast } from "sonner";

const SCORES = [
  ["condition", "Condition"], ["risk", "Risk"], ["utilisation", "Utilisation"], ["environment", "Environmental"],
];

export default function FieldAssetPage() {
  const { assetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fromRegister = location.state?.from === "register";
  const [asset, setAsset] = useState(null);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const photoRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: a }, { data: f }] = await Promise.all([
        api.get(`/capital/assets/${encodeURIComponent(assetId)}`),
        api.get(`/field/assets/${encodeURIComponent(assetId)}/files`)]);
      setLoadErr(null);
      setAsset(a); setFiles(f.files);
      setForm({
        condition: a.condition ?? "", risk: a.risk ?? "", utilisation: a.utilisation ?? "",
        environment: a.environment ?? "", remaining_life_years: a.remaining_life_years ?? "",
        install_year: a.install_year ? String(a.install_year).slice(0, 4) : "",
        asset_name: a.asset_name || "", field_notes: a.field_notes || "",
        manufacturer: a.manufacturer || "", model_number: a.model_number || "",
        serial_number: a.serial_number || "", cust_id: a.cust_id || "",
      });
      f.files.filter((x) => x.is_image).slice(0, 12).forEach(async (x) => {
        try {
          const r = await api.get(`/field/files/${x.file_id}/download`, { responseType: "blob" });
          setThumbs((t) => ({ ...t, [x.file_id]: URL.createObjectURL(r.data) }));
        } catch { /* ignore */ }
      });
    } catch (e) {
      setLoadErr(e.response?.data?.detail || "Asset unavailable — check your connection and try again");
    }
  };
  useEffect(() => { load(); }, [assetId]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    const payload = {};
    ["condition", "risk", "utilisation", "environment", "remaining_life_years"].forEach((k) => {
      if (form[k] !== "" && Number(form[k]) !== Number(asset[k])) payload[k] = Number(form[k]);
    });
    if (form.install_year && String(form.install_year) !== String(asset.install_year || "").slice(0, 4)) payload.install_year = Number(form.install_year);
    if (form.asset_name && form.asset_name !== asset.asset_name) payload.asset_name = form.asset_name;
    ["manufacturer", "model_number", "serial_number", "cust_id"].forEach((k) => {
      if (form[k] !== (asset[k] || "")) payload[k] = form[k];
    });
    if (form.field_notes !== (asset.field_notes || "")) payload.field_notes = form.field_notes;
    try {
      const { data } = await api.patch(`/field/assets/${encodeURIComponent(assetId)}`, payload);
      if (data.changed === 0) {
        toast.success("Asset verified — no changes needed, inspection time recorded");
      } else {
        toast.success(`Saved ${data.changed} field(s) — asset marked field-validated`);
      }
      const wb = data.aroflo_writeback;
      if (wb?.status === "ok") toast.success("Changes synced back to AroFlo");
      else if (wb?.status === "failed") toast.warning("Saved in hub — AroFlo sync failed (logged for review)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/field/assets/${assetId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeFile = async (f) => {
    if (!confirm(`Remove ${f.original_filename}?`)) return;
    try { await api.delete(`/field/files/${f.file_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadLabel = async () => {
    try {
      const r = await api.get(`/field/assets/${assetId}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `label_${assetId.slice(0, 10)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Label download failed"); }
  };

  const openFile = async (f) => {
    try {
      const r = await api.get(`/field/files/${f.file_id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open file"); }
  };

  if (!asset) {
    if (loadErr) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="field-asset-error">
          <div className="text-center max-w-sm">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <div className="text-sm font-semibold text-slate-800">Couldn't open this asset</div>
            <div className="text-xs text-rose-600 mt-1.5">{loadErr}</div>
            <div className="text-[11px] text-slate-500 mt-3">
              If this came from a printed QR label, the label may be from an old batch — please reprint it from the hub.
            </div>
            <Button className="mt-4 h-10 bg-[#003a5c] hover:bg-[#00294a] text-white" data-testid="field-asset-retry"
                    onClick={() => { setLoadErr(null); load(); }}>Try again</Button>
          </div>
        </div>
      );
    }
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center gap-2 text-sm text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin" /> Loading asset…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="field-asset-page">
      {/* Header */}
      <div className="bg-[#003a5c] text-white px-4 py-4">
        {fromRegister && (
          <button type="button" onClick={() => navigate(-1)}
                  data-testid="asset-back-to-register"
                  className="inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white mb-2 -ml-1 px-1 py-0.5 rounded transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Asset Register
          </button>
        )}
        <div className="text-[10px] uppercase tracking-widest opacity-60">GBE Field Capture</div>
        <div className="text-lg font-bold leading-tight mt-0.5">{asset.asset_name || asset.estimated_asset_type}</div>
        <div className="text-xs opacity-80 mt-0.5">{asset.site_name} · {asset.asset_type}{asset.tag_id && <span className="font-mono font-semibold"> · {asset.tag_id}</span>}</div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="inline-flex px-2 py-0.5 rounded-full bg-white/15 text-[10px]">{displayStatus(asset.v8_renewal_status)}</span>
          <ConsequenceBadge a={asset} testid="field-consequence" />
          {asset.field_validated_at && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-[10px]">
              <CheckCircle2 className="w-3 h-3" /> validated {asset.field_validated_at.slice(0, 10)}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-4 mt-4">
        {/* Scores */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold text-slate-800 mb-3">Ratings (1 = best · 5 = worst)</div>
          {SCORES.map(([k, label]) => (
            <div key={k} className="flex items-center gap-2 mb-2.5">
              <span className="text-xs text-slate-600 w-28">{label}</span>
              <div className="flex gap-1.5 flex-1" data-testid={`field-score-${k}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, [k]: n })}
                          data-testid={`field-${k}-${n}`}
                          className={`w-9 h-9 rounded-lg border text-sm font-semibold transition-colors ${
                            Number(form[k]) === n ? "bg-[#003a5c] text-white border-[#003a5c]" : "bg-white text-slate-500 border-slate-200 active:bg-slate-100"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <label className="text-xs text-slate-600">Remaining life (yrs)
              <Input type="number" step="0.5" value={form.remaining_life_years} data-testid="field-remaining-life"
                     onChange={(e) => setForm({ ...form, remaining_life_years: e.target.value })} className="h-9 mt-1" /></label>
            <label className="text-xs text-slate-600">Install year
              <Input type="number" value={form.install_year} data-testid="field-install-year"
                     onChange={(e) => setForm({ ...form, install_year: e.target.value })} className="h-9 mt-1" /></label>
          </div>
          <label className="text-xs text-slate-600 block mt-3">Asset name / location correction
            <Input value={form.asset_name} data-testid="field-asset-name"
                   onChange={(e) => setForm({ ...form, asset_name: e.target.value })} className="h-9 mt-1" /></label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <label className="text-xs text-slate-600">Manufacturer
              <Input value={form.manufacturer} data-testid="field-manufacturer"
                     onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="h-9 mt-1" /></label>
            <label className="text-xs text-slate-600">Model no
              <Input value={form.model_number} data-testid="field-model-no"
                     onChange={(e) => setForm({ ...form, model_number: e.target.value })} className="h-9 mt-1" /></label>
            <label className="text-xs text-slate-600">Serial number
              <Input value={form.serial_number} data-testid="field-serial-no"
                     onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="h-9 mt-1" /></label>
            <label className="text-xs text-slate-600">Cust ID
              <Input value={form.cust_id} data-testid="field-cust-id"
                     onChange={(e) => setForm({ ...form, cust_id: e.target.value })} className="h-9 mt-1" /></label>
          </div>
          <label className="text-xs text-slate-600 block mt-3">Field notes
            <Textarea rows={2} value={form.field_notes} data-testid="field-notes"
                      onChange={(e) => setForm({ ...form, field_notes: e.target.value })} className="mt-1 text-sm" /></label>
          <Button className="w-full mt-4 h-11 bg-gbe hover:bg-gbe-dark text-white" onClick={save} disabled={saving}
                  data-testid="field-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save field update
          </Button>
        </div>

        {/* Photos & files */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-800">Photos & files ({files.length})</span>
            <div className="flex gap-2">
              <input type="file" accept="image/*" capture="environment" ref={photoRef} className="hidden"
                     onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              <input type="file" ref={fileRef} className="hidden"
                     onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              <Button size="sm" variant="outline" className="h-9" disabled={uploading}
                      data-testid="field-photo-btn" onClick={() => photoRef.current?.click()}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="outline" className="h-9" disabled={uploading}
                      data-testid="field-file-btn" onClick={() => fileRef.current?.click()}>
                <Paperclip className="w-4 h-4" /></Button>
            </div>
          </div>
          {files.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-6" data-testid="field-files-empty">
              No photos or files yet — capture the nameplate and general condition.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2" data-testid="field-gallery">
                {files.filter((f) => f.is_image).map((f) => (
                  <div key={f.file_id} className="relative group">
                    {thumbs[f.file_id]
                      ? <img src={thumbs[f.file_id]} alt={f.original_filename} onClick={() => openFile(f)}
                             className="w-full h-24 object-cover rounded-lg border border-slate-200 cursor-pointer" />
                      : <div className="w-full h-24 rounded-lg bg-slate-100 animate-pulse" />}
                    <button onClick={() => removeFile(f)}
                            className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-slate-500 hover:text-rose-600">
                      <Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              {files.filter((f) => !f.is_image).map((f) => (
                <div key={f.file_id} className="flex items-center gap-2 mt-2 text-xs border border-slate-100 rounded-lg px-2 py-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <button className="flex-1 text-left text-gbe hover:underline truncate" onClick={() => openFile(f)}>{f.original_filename}</button>
                  <span className="text-slate-400 text-[10px]">{(f.size / 1024).toFixed(0)}kb</span>
                  <button onClick={() => removeFile(f)} className="text-slate-300 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </>
          )}
        </div>

        <Button variant="outline" className="w-full h-10 text-xs" onClick={downloadLabel} data-testid="field-label-btn">
          <QrCode className="w-4 h-4 mr-2" /> Download QR label (62mm thermal)
        </Button>
      </div>
    </div>
  );
}
